import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, ArrowRight, Banknote, Lock, CheckCircle, AlertTriangle } from 'lucide-react';

const StructureValidation = ({ uploadedFiles = [] }) => {
  const [showReclassification, setShowReclassification] = useState(false);

  const cashData = {
    efectivoDisponible: 738523,
    efectivoRestringido: 2800037,
    totalOriginal: 3538560
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('COP', '$');
  };

  const startReclassification = () => {
    setShowReclassification(true);
  };

  return (
    <div style={{ padding: '24px', width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', color: 'white', padding: '12px', borderRadius: '12px' }}>
          <Banknote size={24} />
        </div>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
            Desglose de Efectivo y Restricciones
          </h2>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '16px' }}>
            Análisis de Efectivo (Nota 4) - Identificación de restricciones
          </p>
        </div>
      </div>

      {/* Original Cash Table */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }}>
        
        {/* Left Side - Original Values */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          style={{
            background: 'white',
            border: '2px solid #e5e7eb',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
          }}
        >
          <h3 style={{ 
            margin: '0 0 20px 0', 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#374151',
            paddingBottom: '12px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            Tabla Original (Valores Agregados)
          </h3>

          <div style={{
            background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
            border: '1px solid #cbd5e1',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <DollarSign size={32} style={{ color: '#64748b', margin: '0 auto' }} />
            </div>
            <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>
              Efectivo y Equivalentes
            </div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b' }}>
              {formatCurrency(cashData.totalOriginal)}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#64748b', 
              marginTop: '8px',
              background: '#e2e8f0',
              padding: '4px 8px',
              borderRadius: '12px',
              display: 'inline-block'
            }}>
              Clasificado como Activo Circulante
            </div>
          </div>
        </motion.div>

        {/* Right Side - Split Visual */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            background: 'white',
            border: '2px solid #e5e7eb',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
          }}
        >
          <h3 style={{ 
            margin: '0 0 20px 0', 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#374151',
            paddingBottom: '12px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            Split Visual Detectado
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            
            {/* Efectivo Disponible */}
            <div style={{
              background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
              border: '2px solid #10b981',
              borderRadius: '12px',
              padding: '16px',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Banknote size={16} style={{ color: '#059669' }} />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#065f46' }}>
                  Efectivo Disponible
                </span>
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#065f46' }}>
                {formatCurrency(cashData.efectivoDisponible)}
              </div>
              <div style={{ fontSize: '12px', color: '#047857', marginTop: '4px' }}>
                Mantiene clasificación circulante
              </div>
            </div>

            {/* Efectivo Restringido */}
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '2px solid #f59e0b',
              borderRadius: '12px',
              padding: '16px',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Lock size={16} style={{ color: '#d97706' }} />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                  Efectivo Restringido
                </span>
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#92400e' }}>
                {formatCurrency(cashData.efectivoRestringido)}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: '#92400e', 
                marginTop: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <AlertTriangle size={12} />
                A mover a no circulante
              </div>
            </div>
          </div>

          {/* Start Reclassification Button */}
          {!showReclassification && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={startReclassification}
              style={{
                width: '100%',
                marginTop: '20px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
            >
              <ArrowRight size={16} />
              Iniciar Reclasificación
            </motion.button>
          )}
        </motion.div>
      </div>

      {/* Reclassification Animation */}
      <AnimatePresence>
        {showReclassification && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              border: '2px solid #3b82f6',
              borderRadius: '16px',
              padding: '32px',
              marginTop: '24px',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Background decoration */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%)',
              transform: 'rotate(45deg)'
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <h3 style={{ 
                margin: '0 0 24px 0', 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#1e40af',
                textAlign: 'center'
              }}>
                Animación de Reclasificación
              </h3>

              {/* Reclassification Flow */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'center' }}>
                
                {/* From */}
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{
                    background: 'white',
                    border: '2px solid #ef4444',
                    borderRadius: '12px',
                    padding: '16px',
                    minWidth: '200px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontSize: '14px', color: '#dc2626', fontWeight: '600', marginBottom: '8px' }}>
                    DESDE: Activo Circulante
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#dc2626' }}>
                    {formatCurrency(cashData.efectivoRestringido)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                    Efectivo y Equivalentes
                  </div>
                </motion.div>

                {/* Arrow */}
                <motion.div
                  animate={{ x: [0, 10, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  style={{ color: '#3b82f6' }}
                >
                  <ArrowRight size={32} />
                </motion.div>

                {/* To */}
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  style={{
                    background: 'white',
                    border: '2px solid #10b981',
                    borderRadius: '12px',
                    padding: '16px',
                    minWidth: '200px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontSize: '14px', color: '#059669', fontWeight: '600', marginBottom: '8px' }}>
                    HACIA: Activo No Circulante
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#059669' }}>
                    {formatCurrency(cashData.efectivoRestringido)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>
                    Efectivo Restringido
                  </div>
                </motion.div>
              </div>

              {/* Success Message */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1 }}
                style={{
                  marginTop: '24px',
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  border: '2px solid #10b981',
                  borderRadius: '12px',
                  padding: '20px',
                  textAlign: 'center'
                }}
              >
                <CheckCircle size={32} style={{ color: '#10b981', marginBottom: '8px' }} />
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>
                  Reclasificación Completada
                </div>
                <div style={{ fontSize: '14px', color: '#047857' }}>
                  Movimiento de {formatCurrency(cashData.efectivoRestringido)} desde "Activo circulante" → "Activo no circulante / Efectivo restringido"
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StructureValidation; 